import { useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  V2Loader,
  VisuallyHidden,
} from "web-design-system";
import { handleError } from "web-core/utils";
import {
  useFinalizePortfolioReview,
  usePortfolioReviewFrozenOverview,
  usePortfolioReviewReport,
} from "creso-core/hooks";
import { PortfolioReview } from "creso-core/types";
import { getPdfDownloadUrl } from "@/utils/common";
import { ReportPreview } from "@/modules/reports/components/report-preview";
import { reviewReportSections } from "@/modules/reports/components/portfolio-review/report-data";

const TITLE = "Portfolio Review Report";

export function ReviewReportDialog({
  review,
  open,
  onOpenChange,
}: {
  review: PortfolioReview;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[90vh] w-[95vw] max-w-[960px] flex-col gap-0 overflow-hidden p-0">
        <VisuallyHidden>
          <DialogHeader>
            <DialogTitle>{TITLE}</DialogTitle>
          </DialogHeader>
        </VisuallyHidden>
        {open ? <ReviewReportPreview review={review} /> : null}
      </DialogContent>
    </Dialog>
  );
}

function ReviewReportPreview({ review }: { review: PortfolioReview }) {
  const { data: report, isLoading: isReportLoading } = usePortfolioReviewReport(
    review.id
  );
  const { data: overview, isLoading: isOverviewLoading } =
    usePortfolioReviewFrozenOverview(review.id);

  // Built once the data is in, so the preview loads its page a single time.
  const sections = useMemo(
    () => (report ? reviewReportSections({ review, report, overview }) : []),
    [review, report, overview]
  );

  return (
    <V2Loader
      className="size-full"
      isLoading={isReportLoading || isOverviewLoading}
    >
      <ReportPreview
        title={TITLE}
        reportUrl={getPdfDownloadUrl(
          `/reports/clients/${review.client_id}/portfolio-review/${review.id}`
        )}
        sections={sections}
        downloadFileName={`${review.client_name ?? "client"}-portfolio-review.pdf`}
        footerActions={<MarkReadyAction review={review} />}
      />
    </V2Loader>
  );
}

function MarkReadyAction({ review }: { review: PortfolioReview }) {
  const [confirming, setConfirming] = useState(false);
  const { mutate: finalize, isPending } = useFinalizePortfolioReview();

  if (review.status !== "DRAFT") return null;

  if (!confirming) {
    return (
      <Button variant="outline" onClick={() => setConfirming(true)}>
        Mark as ready
      </Button>
    );
  }

  return (
    <>
      <span className="mr-auto self-center text-sm text-muted-foreground">
        Marking the review as ready locks it from further edits.
      </span>
      <Button
        variant="outline"
        disabled={isPending}
        onClick={() => setConfirming(false)}
      >
        Cancel
      </Button>
      <Button
        disabled={isPending}
        onClick={() =>
          finalize(review.id, {
            onSuccess: () => setConfirming(false),
            onError: (error) =>
              handleError(error, "Could not finalise the review"),
          })
        }
      >
        {isPending ? <Loader2 className="animate-spin" /> : null}
        Confirm
      </Button>
    </>
  );
}
